import { useEffect, useState } from "react";
import { AlertTriangle, Info, Loader2, Undo2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { errMsg } from "../ui";
import { KIND_SHORT, fmt } from "./meta";

const WHAT = { add: "removed again", move: "moved back", remove: "put back", create_subscription: "cancelled" };

/** Confirm step for undoing applied rows — `target` is {items:[{apply_id, seq}]} or {apply_id} (everything in that apply). */
export function UndoDialog({ target, onClose, onStarted }) {
  const [plan, setPlan] = useState(null);
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.post("/billing-sync/preview/undo/plan", target).then(({ data }) => setPlan(data))
      .catch((e) => { toast.error(errMsg(e, "Could not check what can be undone")); onClose(); });
  }, [target, onClose]);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing-sync/preview/undo", target);
      toast.success(`Undoing ${fmt(data.total)} change${data.total === 1 ? "" : "s"} in Stax Bill`);
      onStarted(data.id);
    } catch (e) { toast.error(errMsg(e, "Could not start the undo")); setBusy(false); }
  };
  const subs = plan?.by_kind?.create_subscription || 0;
  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-lg" data-testid="bsp-undo-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Undo in Stax Bill</DialogTitle>
          <DialogDescription>Each change is reversed straight away. Anything that changed in Stax Bill since the apply is skipped.</DialogDescription>
        </DialogHeader>
        {!plan ? <Loader2 className="mx-auto my-8 h-5 w-5 animate-spin text-slate-300" /> : (
          <div className="space-y-3">
            <ul className="space-y-1 text-sm text-slate-700" data-testid="bsp-undo-plan">
              {Object.entries(plan.by_kind).map(([k, n]) => (
                <li key={k} data-testid={`bsp-undo-plan-${k}`}><b className="tabular-nums">{fmt(n)}</b> {KIND_SHORT[k].toLowerCase()}{n === 1 ? "" : "s"} {WHAT[k]}</li>))}
            </ul>
            {subs > 0 && (
              <div className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-900" data-testid="bsp-undo-sub-warning">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /><div>Cancelling {subs === 1 ? "a subscription" : `${subs} subscriptions`} ends billing for every item on {subs === 1 ? "it" : "them"}. Items the apply had moved onto {subs === 1 ? "it" : "them"} are put back where they were.</div></div>)}
            <div className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600"><Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <div>An undone row shows up again in its preview, so it can be applied again later.</div></div>
            {plan.total === 0 ? <p className="text-sm text-slate-600" data-testid="bsp-undo-empty">Nothing left to undo — these rows were already undone.</p> : (
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <Checkbox checked={ok} onCheckedChange={(v) => setOk(!!v)} data-testid="bsp-undo-confirm-check" />
                I understand this changes billing in Stax Bill</label>)}
          </div>)}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy} data-testid="bsp-undo-cancel">Cancel</Button>
          <Button onClick={go} disabled={!plan || !plan.total || !ok || busy} className="bg-slate-900" data-testid="bsp-undo-confirm">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Undo2 className="h-4 w-4" />} Undo {fmt(plan?.total)} change{plan?.total === 1 ? "" : "s"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
